import { useState, useEffect, useCallback } from "react"
import { Users, Eye, EyeOff, Edit, Trash2, ChevronDown, ChevronUp, Search, UserPlus, Loader2 } from "lucide-react"
import { toast } from "react-hot-toast"
import { AddEmployeeModal } from "../components/add-employee-modal"
import { EditEmployeeModal } from "../components/edit-employee-modal"
import { DeleteEmployeeModal } from "../components/delete-employee-modal"
import EmployeeDetailsModal from "../components/EmployeeDetailsModal"
import DateRangePicker from '../components/ui/date-range-picker'
import { Button } from "../components/ui/button"
import { employeeManagementService } from "../apis/employeeManagement";
import { userManagementService } from "../apis/userManagement"
import { defaultDateRange, formatInAppTz } from "../utils/timezone"

// Helper functions for date handling
const getDefaultDateRange = () => ({
    ...defaultDateRange(),
    label: "Today",
});

const formatDateForAPI = (date) => formatInAppTz(date, "MM/DD/YYYY hh:mm A");

export default function EmployeeManagement() {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [roles, setRoles] = useState([]);
    const [selectedRole, setSelectedRole] = useState(""); // Single role selection
    const [expandAllCards, setExpandAllCards] = useState(false);
    const [expandedCards, setExpandedCards] = useState(new Set());
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [deleteEmployee, setDeleteEmployee] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState({ add: false, edit: false, delete: false });
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [showEmployeeDetailsModal, setShowEmployeeDetailsModal] = useState(false);
    const [loadingEmployeeDetails, setLoadingEmployeeDetails] = useState(false);
    const [employeeDetailsError, setEmployeeDetailsError] = useState(null);
    const [employeeDetails, setEmployeeDetails] = useState(null);
    const [detailsEmployeeName, setDetailsEmployeeName] = useState("");

    // Initialize with default date range (today)
    const [dateRange, setDateRange] = useState(getDefaultDateRange());

    // Fetch employee data
    const fetchEmployees = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                startDate: formatDateForAPI(dateRange.start),
                endDate: formatDateForAPI(dateRange.end)
            };

            const result = await employeeManagementService.getAllEmpData(params);

            if (result.success && result.data?.body?.data?.employees) {
                // Transform API data to match UI structure
                const transformed = result.data.body.data.employees.map(emp => ({
                    id: emp.id.toString(),
                    name: emp.name,
                    role: emp.role,
                    // Header values
                    overallTipPool: Math.round(emp.total_amount - emp.payout_total),
                    overallTokes: Math.round(emp.total_tax),
                    overallPaidOut: Math.round(emp.total_amount - emp.total_tax),
                    workingHours: Number(emp.working_hours) || 0,
                    morningTips: Number(emp.morning_tips) || 0,
                    eveningTips: Number(emp.evening_tips) || 0,
                    unassignedShiftTips: Number(emp.unassigned_shift_tips) || 0,
                    // Role details
                    dealer: {
                        total: Math.round(emp.dealer_total),
                        tax: Math.round(emp.dealer_tax),
                        paidOut: Math.round(emp.dealer_total - emp.dealer_tax)
                    },
                    waitress: {
                        total: Math.round(emp.waitress_total),
                        tax: Math.round(emp.waitress_tax),
                        paidOut: Math.round(emp.waitress_total - emp.waitress_tax)
                    },
                    cashier: {
                        total: Math.round(emp.cashier_total),
                        tax: Math.round(emp.cashier_tax),
                        paidOut: Math.round(emp.cashier_total - emp.cashier_tax)
                    },
                    floorsupervisor: {
                        total: Math.round((Number(emp.floorsupervisor_total) || 0) + (Number(emp.bartender_total) || 0)),
                        tax: Math.round((Number(emp.floorsupervisor_tax) || 0) + (Number(emp.bartender_tax) || 0)),
                        paidOut: Math.round(
                            ((Number(emp.floorsupervisor_total) || 0) + (Number(emp.bartender_total) || 0)) -
                            ((Number(emp.floorsupervisor_tax) || 0) + (Number(emp.bartender_tax) || 0))
                        )
                    },
                    hosts: {
                        total: Math.round(emp.hosts_total),
                        tax: Math.round(emp.hosts_tax),
                        paidOut: Math.round(emp.hosts_total - emp.hosts_tax)
                    },
                    payouts: Math.round(emp.payout_total)
                }));

                setEmployees(transformed);
            }
        } catch (err) {
            console.error("Error fetching employees:", err);
            toast.error("Failed to load employee data");
        } finally {
            setLoading(false);
        }
    }, [dateRange]);

    // Fetch roles
    const fetchRoles = async () => {
        try {
            const result = await userManagementService.getRoles();
            if (result.success && result.data?.body?.data) {
                const rolesList = result.data.body.data;
                const excludedRoles = ["Admin", "Management", "Manager"];
                const extractedRoles = rolesList
                    .filter(roleObj => !excludedRoles.includes(roleObj.role))
                    .map(roleObj => roleObj.role);
                // Removed the line that adds "All Roles" to the beginning
                setRoles(extractedRoles);
            }
        } catch (err) {
            console.error("Error fetching roles:", err);
            toast.error("Failed to load roles data");
        }
    };

    useEffect(() => {
        fetchEmployees();
        fetchRoles();
    }, [fetchEmployees]);

    const handleRoleChange = (newRole) => {
        setSelectedRole(newRole);
    };

    const toggleAllCards = () => {
        if (expandAllCards) {
            // Collapse all cards
            setExpandedCards(new Set());
            setExpandAllCards(false);
        } else {
            // Expand all cards
            const allEmployeeIds = new Set(filteredEmployees.map(emp => emp.id));
        setExpandedCards(allEmployeeIds);
        setExpandAllCards(true);
        }
    };

    const handleDateRangeApply = (startDate, endDate, label) => {
        setDateRange({ start: startDate, end: endDate, label });
        setIsDatePickerOpen(false);
    };

    const handleAddEmployee = async (employeeData) => {
        try {
            setActionLoading(prev => ({ ...prev, add: true }));
            const result = await employeeManagementService.addEmployee(employeeData);
            if (result.success) {
                setIsAddDialogOpen(false);
                await fetchEmployees();
                toast.success("Employee added successfully");
            } else {
                toast.error("Failed to add employee");
            }
        } catch (err) {
            console.error("Error adding employee:", err);
            toast.error("Failed to add employee");
        } finally {
            setActionLoading(prev => ({ ...prev, add: false }));
        }
    };

    const handleUpdateEmployee = async (updatedEmployeeData) => {
        try {
            setActionLoading(prev => ({ ...prev, edit: true }));
            const result = await employeeManagementService.updateEmployee(
                editingEmployee.id,
                updatedEmployeeData
            );
            if (result.success) {
                setEditingEmployee(null);
                await fetchEmployees();
                toast.success("Employee updated successfully");
            } else {
                toast.error("Failed to update employee");
            }
        } catch (err) {
            console.error("Error updating employee:", err);
            toast.error("Failed to update employee");
        } finally {
            setActionLoading(prev => ({ ...prev, edit: false }));
        }
    };

    const confirmDeleteEmployee = async () => {
        if (deleteEmployee) {
            try {
                setActionLoading(prev => ({ ...prev, delete: true }));
                const result = await employeeManagementService.deleteEmployee(deleteEmployee.id);
                if (result.success) {
                    setIsDeleteModalOpen(false);
                    setDeleteEmployee(null);
                    await fetchEmployees();
                    toast.success("Employee deleted successfully");
                } else {
                    toast.error("Failed to delete employee");
                }
            } catch (err) {
                console.error("Error deleting employee:", err);
                toast.error("Failed to delete employee");
            } finally {
                setActionLoading(prev => ({ ...prev, delete: false }));
            }
        }
    };

    const toggleCardExpansion = (id) => {
        const newExpanded = new Set(expandedCards);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
            // Check if all cards are now collapsed
            if (expandAllCards) {
                setExpandAllCards(false);
            }
        } else {
            newExpanded.add(id);
        }
        setExpandedCards(newExpanded);
    };

    const openEmployeeDetails = async (employee, e) => {
        if (e) e.stopPropagation();
        setDetailsEmployeeName(employee.name);
        setLoadingEmployeeDetails(true);
        setEmployeeDetailsError(null);
        setShowEmployeeDetailsModal(true);
        setEmployeeDetails(null);

        try {
            const startDate = formatInAppTz(dateRange.start, "YYYY-MM-DD");
            const endDate = formatInAppTz(dateRange.end, "YYYY-MM-DD");
            const [completeDataResponse, performanceResponse] = await Promise.all([
                employeeManagementService.getCompleteEmployeeData(employee.id, { startDate, endDate }),
                employeeManagementService.getEmployeePerformanceMetrics(employee.id, { startDate, endDate })
            ]);

            if (completeDataResponse.success && performanceResponse.success) {
                setEmployeeDetails({
                    employee,
                    completeData: completeDataResponse?.data?.body?.data,
                    performanceMetrics: performanceResponse?.data?.body?.data
                });
            } else {
                setEmployeeDetailsError("Failed to load employee details");
            }
        } catch (err) {
            console.error("Error loading employee details:", err);
            setEmployeeDetailsError("Error loading employee details");
        } finally {
            setLoadingEmployeeDetails(false);
        }
    };

    const closeEmployeeDetailsModal = () => {
        setShowEmployeeDetailsModal(false);
        setEmployeeDetails(null);
        setEmployeeDetailsError(null);
        setDetailsEmployeeName("");
    };

    // Filter employees based on search and selected role
    const filteredEmployees = employees.filter(employee => {
        const matchesSearch = employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            employee.role.toLowerCase().includes(searchTerm.toLowerCase());

        // If no role is selected, show all employees
        const matchesRole = selectedRole === "" ||
            employee.role.split(',').some(r => r.trim() === selectedRole);

        return matchesSearch && matchesRole;
    });

    if (loading) {
        return (
            <div className="p-6 bg-gray-50">
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 p-6">
 
                {/* Header */}
                <div className="relative mb-8 flex items-center justify-center">
                    <div className="absolute left-0">
                        <Button
                            onClick={() => setIsAddDialogOpen(true)}
                            disabled={actionLoading.add}
                            className="bg-yellow-400 hover:bg-yellow-500 text-black"
                        >
                            {actionLoading.add ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <UserPlus className="mr-2 h-4 w-4" />
                            )}
                            Add Employee
                        </Button>
                    </div>

                    <h1 className="text-3xl font-bold text-gray-800 text-center">Employee Management</h1>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-yellow-400 rounded-full flex items-center justify-center">
                        <Users className="w-6 h-6 text-gray-800" />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-sm text-gray-600">Date Range:</span>
                        <button
                            onClick={() => setIsDatePickerOpen(true)}
                            className="text-sm bg-white border rounded px-3 py-1 hover:bg-gray-50 text-left transition-colors"
                        >
                            {dateRange.label}
                        </button>
                    </div>
                    <div className="flex flex-col">
                        <span className="text-sm text-gray-600">
                            {selectedRole ? `${selectedRole} Employees:` : "Total Employees:"}
                        </span>
                        <span className="text-sm font-semibold text-gray-800">
                            {filteredEmployees.length}
                        </span>
                    </div>
                </div>

                {/* Date Picker */}
                {isDatePickerOpen && (
                    <DateRangePicker
                        isOpen={isDatePickerOpen}
                        onClose={() => setIsDatePickerOpen(false)}
                        onApply={handleDateRangeApply}
                        initialStartDate={dateRange.start}
                        initialEndDate={dateRange.end}
                    />
                )}

                {/* Search and Filter */}
                <div className="flex items-center gap-4 mb-6 flex-wrap">
                    <div className="flex-1 relative min-w-64">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 w-4" />
                        <input
                            type="text"
                            placeholder="Search by employee name or role"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <select
                        value={selectedRole}
                        onChange={(e) => handleRoleChange(e.target.value)}
                        className="w-48 px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All Roles ({employees.length})</option>
                        {roles.map((role) => {
                            const roleCount = employees.filter((employee) =>
                                employee.role.split(',').some((r) => r.trim() === role)
                            ).length;
                            return (
                                <option key={role} value={role}>
                                    {role} ({roleCount})
                                </option>
                            );
                        })}
                    </select>

                    <button
                        onClick={toggleAllCards}
                        className="p-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors"
                        title={expandAllCards ? "Collapse all cards" : "Expand all cards"}
                    >
                        {expandAllCards ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                    {filteredEmployees.map((employee) => (
                        <div 
                            key={employee.id} 
                            className="bg-yellow-100 border border-yellow-200 rounded-lg p-4 cursor-pointer hover:bg-yellow-200 transition-colors"
                            onClick={() => toggleCardExpansion(employee.id)}
                        >
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                                        <Users className="w-4 h-4 text-white" />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-800">
                                            Employee ID: <span className="text-red-600">{employee.id}</span>
                                            <br />
                                            Name: <span className="text-blue-600">{employee.name}</span>
                                            {" | "}
                                            Working Hrs:{" "}
                                            <span className="text-blue-700">
                                                {Number(employee.workingHours || 0).toFixed(1)} hrs
                                            </span>
                                        </div>
                                        <div className="text-sm text-gray-600">
                                            Role: <span className="text-blue-600">{employee.role}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setEditingEmployee(employee);
                                        }}
                                        disabled={actionLoading.edit}
                                        className="h-8 w-8 flex items-center justify-center text-blue-600 hover:bg-blue-100 rounded transition-colors disabled:opacity-50"
                                    >
                                        {actionLoading.edit && editingEmployee?.id === employee.id ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Edit className="w-4 h-4" />
                                        )}
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDeleteEmployee(employee);
                                            setIsDeleteModalOpen(true);
                                        }}
                                        disabled={actionLoading.delete}
                                        className="h-8 w-8 flex items-center justify-center text-red-600 hover:bg-red-100 rounded transition-colors disabled:opacity-50"
                                    >
                                        {actionLoading.delete && deleteEmployee?.id === employee.id ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Trash2 className="w-4 h-4" />
                                        )}
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleCardExpansion(employee.id);
                                        }}
                                        className="h-8 w-8 flex items-center justify-center hover:bg-gray-100 rounded transition-colors"
                                    >
                                        {expandedCards.has(employee.id) ? (
                                            <ChevronUp className="w-4 h-4" />
                                        ) : (
                                            <ChevronDown className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-1 text-sm">
                                <div>
                                    Overall Tip Pool: <span className="font-semibold">{employee.overallTipPool}</span>
                                </div>
                                <div>
                                    Overall Tokes: <span className="font-semibold">{employee.overallTokes}</span>
                                </div>
                                <div>
                                    Overall Paid Out: <span className="font-semibold">{employee.overallPaidOut}</span>
                                </div>
                                <div>
                                    Morning Tips (12:00 PM–5:59 PM):{" "}
                                    <span className="font-semibold text-green-700">
                                        ${employee.morningTips.toFixed(2)}
                                    </span>
                                    {" | "}
                                    Evening Tips (6:00 PM–5:59 AM):{" "}
                                    <span className="font-semibold text-purple-700">
                                        ${employee.eveningTips.toFixed(2)}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={(e) => openEmployeeDetails(employee, e)}
                                    className="mt-2 text-sm text-blue-700 underline hover:text-blue-900"
                                >
                                    View full tip & payroll details
                                </button>
                            </div>

                            {expandedCards.has(employee.id) && (
                                <div className="mt-4 pt-4 border-t border-yellow-300">
                                    <div className="mb-3 text-sm">
                                        <div className="font-semibold text-gray-800 mb-1">Shift Tip Comparison</div>
                                        <div>
                                            Morning (12:00 PM–5:59 PM CT): ${employee.morningTips.toFixed(2)} | Evening (6:00 PM–5:59 AM CT): ${employee.eveningTips.toFixed(2)}
                                            {employee.unassignedShiftTips > 0 && (
                                                <span className="text-gray-500">
                                                    {" "}| Unassigned: ${employee.unassignedShiftTips.toFixed(2)}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-gray-600 mt-1">
                                            {employee.eveningTips > employee.morningTips
                                                ? "Evening shifts are currently more tip-encouraging."
                                                : employee.morningTips > employee.eveningTips
                                                    ? "Morning shifts are currently more tip-encouraging."
                                                    : "Morning and evening tip totals are similar."}
                                        </div>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full">
                                            <tbody className="divide-y divide-yellow-300">
                                                {/* Render role details */}
                                                {employee.role.split(',').map(role => {
                                                    const normalizedRole = role.trim().toLowerCase().replace(/\s+/g, '');
                                                    const roleKey = normalizedRole === 'bartender' ? 'floorsupervisor' : normalizedRole;
                                                    const roleData = employee[roleKey];
                                                    const roleLabel = normalizedRole === 'bartender' ? 'Floor' : role.trim();

                                                    if (!roleData) return null;

                                                    return (
                                                        <tr key={`${roleKey}-${roleLabel}`}>
                                                            <td className="py-2 pr-4">{roleLabel} Tips</td>
                                                            <td className="py-2">
                                                                {roleData.total.toLocaleString()}
                                                                <span className="text-red-500 block">- {roleData.tax.toLocaleString()}</span>
                                                            </td>
                                                            <td className="py-2 text-right">
                                                                Paid Out: {roleData.paidOut.toLocaleString()}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}

                                                {/* Payouts row */}
                                                <tr>
                                                    <td className="py-2 pr-4">Payouts</td>
                                                    <td className="py-2" colSpan={2}>
                                                        Paid Out: {employee.payouts.toLocaleString()}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* No Results */}
                {filteredEmployees.length === 0 && !loading && (
                    <div className="text-center py-12">
                        <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-600 mb-2">No employees found</h3>
                        <p className="text-gray-500">
                            Try adjusting your search, filter criteria, or date range
                        </p>
                    </div>
                )}

                {/* Modals */}
                <AddEmployeeModal
                    isOpen={isAddDialogOpen}
                    onClose={() => setIsAddDialogOpen(false)}
                    onAddEmployee={handleAddEmployee}
                    isLoading={actionLoading.add}
                    roles={roles} // This will now not include "All Roles"
                />

                <EditEmployeeModal
                    isOpen={!!editingEmployee}
                    onClose={() => setEditingEmployee(null)}
                    employee={editingEmployee}
                    onUpdateEmployee={handleUpdateEmployee}
                    isLoading={actionLoading.edit}
                    roles={roles}
                />

                <DeleteEmployeeModal
                    isOpen={isDeleteModalOpen}
                    onConfirm={confirmDeleteEmployee}
                    onCancel={() => {
                        setIsDeleteModalOpen(false);
                        setDeleteEmployee(null);
                    }}
                    employee={deleteEmployee}
                    isLoading={actionLoading.delete}
                />

                <EmployeeDetailsModal
                    isOpen={showEmployeeDetailsModal}
                    onClose={closeEmployeeDetailsModal}
                    loading={loadingEmployeeDetails}
                    error={employeeDetailsError}
                    employeeDetails={employeeDetails}
                    dateRange={dateRange}
                    fallbackName={detailsEmployeeName}
                />
          
        </div>
    );
}